import { h, render, Fragment } from 'preact';
import { useEffect, useState, useMemo } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import Image from "utils/image";
import Icon from "utils/icon";
import ProductGallery from './product-gallery';
import ProductForm from './product-form';
import ProductAccordions from './product-accordions';
import ProductBadges from './product-badges';
import './print.css'

const Price = ({ currentVariant, product, isB2B, quantity }) => {
  if (!currentVariant) {
    return (
      <div>
        {window.TMW.Strings.product.price.noPrice}
      </div>
    );
  }

  
  const [effectiveQuantity, setEffectiveQuantity] = useState(quantity);

  useEffect(() => {
    setEffectiveQuantity(quantity);
  }, [quantity]);

  const compareAgainstRetailPrice = 
  isB2B && currentVariant?.retailPrice?.amount > 0;

  const calculatePrice = (basePrice) => {

    if (!product.sellInSets) {
      return {
        amount: basePrice?.amount || 0,
        currencyCode: basePrice?.currencyCode || 
        currentVariant?.price.currencyCode,
      };
    }
  
    const multiplier = Math.max(
      effectiveQuantity, parseInt(product.sellInSets, 10) || 1
    );
  
    return {
      amount: multiplier * (basePrice?.amount || 0),
      currencyCode: basePrice?.currencyCode || 
      currentVariant?.price.currencyCode,
    };
  };

  const price = () => {
    const className = compareAgainstRetailPrice
      ? 'text-dark-green ml-2'
      : currentVariant.compare_at_price?.amount
      ? 'text-red ml-2'
      : '';

    const priceObject = calculatePrice(currentVariant?.price);

    return (
      <span className={`price ${className}`}>
        {compareAgainstRetailPrice 
          ? `${product.quantityPriceBreaksConfigured ? 'FROM ' : ''}${formatMoney(priceObject || currentVariant?.price)} ${window.TMW.Strings.product.b2b}`
          : formatMoney(priceObject || currentVariant?.price)}
      </span>
    );
  };

  const compareAtPrice = () => {
    const baseAmount = compareAgainstRetailPrice
      ? currentVariant?.retailPrice
      : currentVariant.compare_at_price;

    const compareAtPriceObject = baseAmount ? calculatePrice(baseAmount) : null;
   
    return compareAtPriceObject?.amount ? (
      <span className="compareAtPrice">
        <del>{formatMoney(compareAtPriceObject)}</del>
      </span>
    ) : null;
  };

  return (
    <div>
      {compareAtPrice()}
      {price()}
    </div>
  );
};


const ProductName = ({currentVariant}) => (
  <h1 className="uppercase h3 md:hidden">{currentVariant?.title}</h1>
);

export default (element, data) => {
  const body = document.querySelector('body');

  const Product = ({ product, isB2B }) => {
    const portraitImages = product.portrait

    const [isMobile, setIsMobile] = useState(!data.layout.isAboveBreakpoint('lg'));
    const allVariants = product.variants
    const [selectedOptions, setSelectedOptions] = 
      useState(product.variantBySelectedOptions.selectedOptions);
    // Lookup variant based on selected options
    const currentVariant =
      product.variants.find(
        (v) =>
          JSON.stringify(v.selectedOptions) ===
          JSON.stringify(selectedOptions),
      );
    
    const [quantity, setQuantity] = useState(
      (currentVariant.quantityRule?.min || 1)
    ); 
      
    useEffect(() => {
      setQuantity(currentVariant?.quantityRule?.min || 1)
    }, [currentVariant])

    const price = useMemo(
      () => <Price 
				currentVariant={currentVariant} 
				product={product} 
				isB2B={isB2B} 
        quantity={quantity} />,
      [currentVariant, product, quantity],
    );

    const productName = useMemo(
      () => <ProductName currentVariant={product} />,
      [product],
    );

    const getAvailabilityMatrix = () => {
      // Create base matrix with empty values
      const matrix = product.options.reduce((a, c) => {
        // eslint-disable-next-line no-param-reassign, no-return-assign
        a[c.name] = c.values.reduce((x, y) => { x[y] = ''; return x; }, {});
        return a;
      }, {});

      // Iterate over empty matrix and populate availability values
      // The availability of each product option is cumulative and is dependent upon the
      // selections made in the previous product options
      for (let i = 0; i < Object.entries(matrix).length; i+=1) {
        const [option, value] = Object.entries(matrix)[i];
        const filterCondition = (v, val) => {
          const matchingValuesCondition1 = (k) =>
            v.selectedOptions[k].value === val;
          const matchingValuesCondition2 = (k) =>
            v.selectedOptions[k].value === selectedOptions[k].value;

          switch (i) {
            case 1:
              return matchingValuesCondition1(i)
                && matchingValuesCondition2(i - 1);
            case 2:
              return matchingValuesCondition1(i)
                && matchingValuesCondition2(i - 1)
                && matchingValuesCondition2(i - 2);
            default:
              return matchingValuesCondition1(i);
          }
        };

        for (let j = 0; j < Object.keys(value).length; j+=1) {
          const val = Object.keys(value)[j];
          const valueAvailable = product.variants
            // Filter for relevant variants
            .filter(v => filterCondition(v, val))
            // Check if available
            .reduce((a, c) => (a || c.availableForSale), false);
          // Set value in matrix
          matrix[option][val] = valueAvailable;
        }
      }

      return matrix;
    };

    // Set the initial availability matrix state
    const [availabilityMatrix, setAvailabilityMatrix] =
      useState(getAvailabilityMatrix());

    useEffect(() => {
      // Update the availability matrix when new options are selected
      setAvailabilityMatrix(getAvailabilityMatrix());
    }, [selectedOptions]);

    const filterMedia = () => {
      if (currentVariant.use_variant_images && currentVariant.variant_images) {
        return currentVariant.variant_images;
      }

      let filteredMedia = [];
      let loopFlag = false;
      const currentVariantImage = currentVariant.image    
      let nextVariantImage = ""
      for (let i = 0; i < allVariants.length; i+=1){
        if(currentVariant === allVariants[i]){
          nextVariantImage = allVariants[i+1] && allVariants[i+1].image
          break
        }
      }
      // this means that there are no more images in 
      // product media gallery so we set nextVariantImage to the first
      // image from the gallery
      if(!nextVariantImage){
        nextVariantImage = allVariants[0].image
      }
      
      product.media.forEach((mediaItem) => {
        if(loopFlag){
          if(mediaItem.src === nextVariantImage){
            loopFlag = false
          }
          else if(mediaItem.src !== nextVariantImage){
            filteredMedia.push(mediaItem)
          }
        }
        if(mediaItem.src === currentVariantImage){
          filteredMedia.push(mediaItem)
          loopFlag = true 
        }
      })
      if(filteredMedia.length === 0 && allVariants.length === 1) {
        filteredMedia = product.media
      }
      return filteredMedia
    }

    const [media, setMedia] =
      useState(filterMedia());

    useEffect(() => {
      setMedia(filterMedia());
    }, [currentVariant])

    const checkMobile = () => {
      if(data.layout.isAboveBreakpoint('lg')){
        setIsMobile(false)
      } else {
        setIsMobile(true)
      }
    }

    // eslint-disable-next-line arrow-body-style
    const parseDimensions = (dimensionsString) => {
      if (!dimensionsString) {
        return [];
      }
    
      return dimensionsString.split(';')
        .map((originalPart, index) => {
          const part = originalPart.trim(); // Use a new variable instead of modifying the parameter
          if (!part) return null;
    
          if (index === 0 && !part.includes(':')) {
            return { key: 'Dimensions', value: part };
          }
    
          const [key, value] = part.split(/:(.+)/);
          return { key: key ? key.trim() : '', value: value ? value.trim() : '' };
        })
        .filter(part => part);
    };


    const productDimensionsArray = useMemo(() =>
        parseDimensions(product.dimensions), 
        [product.dimensions] 
    );
    const varianDimensionsArray = useMemo(() =>
        parseDimensions(currentVariant.dimensions), 
        [currentVariant.dimensions] 
    );

    // eslint-disable-next-line arrow-body-style
    const renderDimensions = () => {
      let dimensions = varianDimensionsArray;
      if (productDimensionsArray?.length) {
        dimensions = productDimensionsArray;
      }
      return dimensions?.map(({ key, value }, index) => (
        <div key={index} className='flex gap-x-2 py-3.5 text-10'>
          <div className="font-bold uppercase w-32 h-4">{key}</div>
          <div className='flex-grow'>{value}</div>
        </div>
      ));
    };
    
    const init = () => {
      window.addEventListener('bpChange', checkMobile);
    };

    init()

    return (
      <>
        <div className="lg:flex print:hidden">
          <div className="lg:w-[65%] lg:pl-7">
            <div className='px-3 md:hidden mb-4 mt-8'>
              {product?.badges?.length ? <ProductBadges badges={product?.badges} /> : ''} 
              {productName}
              {price}
            </div>
            <ProductGallery 
              product={product}
              media={media}
              isMobile={isMobile}
              portraitImages={portraitImages}
            />
          </div>
          <div className='hidden atc-sticky:block atc-sticky:fixed atc-sticky:w-full atc-sticky:top-0 lg:atc-sticky:py-0 atc-sticky:z-20 lg:w-[35%] lg:py-14' data-atc-wrapper>
            <ProductForm 
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              availabilityMatrix={availabilityMatrix}
              currentVariant={currentVariant}
              product={product}
							isB2B={isB2B}
              quantity={quantity}
              setQuantity={setQuantity}
              atcSticky
            />
          </div>
          <div className='lg:w-[35%] lg:py-14'>
            <ProductForm 
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              availabilityMatrix={availabilityMatrix}
              currentVariant={currentVariant}
              product={product}
							isB2B={isB2B}
              showTrigger={true}
              quantity={quantity}
              setQuantity={setQuantity}
            />
            <ProductAccordions 
              product={product} 
              currentVariant={currentVariant} 
            />
          </div>
        </div>

        <div className="hidden print:block px-14">
          <div className='mb-4 mt-8 flex justify-between relative items-center'>
            <div className='self-end justify-items-start text-10'>
              <p>{product.blocks.find(block => block.blockType === 'contact')?.phone}</p>
            </div>
            <div className='w-78 h-10 mb-2 absolute inset-0 mx-auto my-auto'>
              <a href="/" className="left-0 opacity-100 transition-opacity duration-300 text-black"
                data-header-logo
              >
                <Icon name="logo"/>
              </a>
            </div>
            <div className='self-end justify-items-end text-10'>
              <p>{product.blocks.find(block => block.blockType === 'contact')?.email}</p>
            </div>
          </div>
          <Image
            classes="h-60 mx-auto mb-12"
            src={currentVariant?.image || product?.images[0]?.src }
            alt={product.images[0]?.alt}
            loadingBehavior={true}
          />    
          <div className='border-y divide-y'>
            <div className='flex gap-x-2 py-3.5 text-10'>
              <p className="font-bold w-32 h-4">{window.TMW.Strings.product.specs.productName}</p>
              <p>{product.title}</p>
            </div>

            <div className='flex gap-x-2 py-3.5 text-10'>
              <p className="font-bold w-32 h-4">{window.TMW.Strings.product.specs.price}</p>
              <p>{price}</p>
            </div>
            {
              product.shippingStatus &&
              <div className='flex gap-x-2 py-3.5 text-10'>
                <p className="font-bold w-32 h-4">{window.TMW.Strings.product.specs.shippingStatus}</p>
                <p>{product.shippingStatus}</p>
              </div>
            }
            {
              currentVariant.selectedOptions.map(option => (
                  option.name !== "Title" && (
                    <div key={option.id} className='flex gap-x-2 py-3.5 text-10'>
                      <p className="font-bold w-32 h-4">{option.name}</p>
                      <p>{option.value}</p>
                    </div>
                  )
                ))
            }
            {
               renderDimensions() 
            }
            {
              currentVariant.sku &&
              <div className='flex gap-x-2 py-3.5 text-10'>
                <p className="font-bold w-32 h-4">{window.TMW.Strings.product.sku}</p>
                <p>{currentVariant.sku}</p>
              </div>
            }
            {
              product.description &&
              <div className='flex gap-x-16 py-3.5 text-10'>
                <div className="font-bold w-32 h-4">{window.TMW.Strings.product.specs.description}</div>
                <div className='flex-grow' dangerouslySetInnerHTML={{__html: product.description}}></div>
              </div>
            }
          </div>      
        </div>    
      </>
    )
  }

  const removeAtcStickky = () => {
    body.classList.remove('atc-sticky');
  };

  const atcSticky = () => {
    const addToCartWrapper = element.querySelector(
      '[data-target="sticky-atc-trigger"]', 
    );
    if (!addToCartWrapper) return;

    const { enableDesktopStickyATC } = JSON.parse(document.querySelector('[data-technical]').textContent);
    const topOffset = addToCartWrapper.getBoundingClientRect().top;

    if (enableDesktopStickyATC && topOffset <= 0) {
      body.classList.add('atc-sticky');
    } else {
      body.classList.remove('atc-sticky');
    }
  };

  const setEmptyFormHeight = () => {
    const wrapper = element.querySelector('[data-empty-form]');
    const form = element.querySelector('#add-to-cart-form');
    if (!wrapper || !form) return;
    const height = form.offsetHeight;
    wrapper.style.height = `${height}px`;
  };

  const init = () => {
    render(<Product {...data} />, element);
    setTimeout(() => {
      setEmptyFormHeight();
    }, 0);
    window.document.addEventListener('scroll', atcSticky);

    window.addEventListener('resize', () => {
      removeAtcStickky();
      setEmptyFormHeight();
    });
  };

  init();
};
